import { PlayerProjectionFragment } from '@/api/events/query.generated';
import { EventInfo } from '@/api/events/types/types';
import {
    BetslipPick,
    InvalidPicksByEvent,
    PickAndEventWithStatus,
    PickWithStatus,
} from '@/feature/betslip-pickem/types';
import { MarketStatus, ProjectionType } from '@/types/api.generated';
import { shouldKeepFantasyLiveRemovedMarkets } from '@/utils/ab-testing';
import { logger } from '@/utils/logging';
import { areProjectionsMatching, getDisplayedProjectionValue } from '@/utils/projections';

import { eventUtils } from './event-utils';

export type ProjectionUpdateStatus = {
    projection: PlayerProjectionFragment;
} & (
    | {
          status: 'value-changed';
          newValue?: number;
      }
    | {
          status: 'type-changed';
          newType?: ProjectionType;
      }
    | {
          status:
              | 'removed'
              | 'suspended'
              | 'resumed'
              | 'outcome-removed'
              | 'valid'
              | 'event-removed'
              | 'market-id-change';
      }
);
export type ProjectionInvalidReason = ProjectionUpdateStatus['status'];
const LOG_TAG = '[SyncPicks]';

/**
 * There are no projections sent to the app with value 0
 */
export const getNewProjValue = (projVal?: number, newProjVal?: number): number | undefined => {
    if (!projVal || !newProjVal) {
        return undefined;
    }
    return projVal !== newProjVal ? newProjVal : undefined;
};

/**
 * Returns the changed status of the betslip pick based on newly fetched event info
 * @param pick the pick to check against
 * @param event new event info data, containing the pick player
 * @returns the status of the pick projection
 */
export const getProjectionUpdateStatus = (pick: BetslipPick, event: EventInfo): ProjectionUpdateStatus => {
    const players = eventUtils.getAllPlayers(event);
    const playerProjections = players?.find(player => pick.player.id === player.id);
    return getProjectionUpdateStatusFromProjections(pick, playerProjections?.projections);
};

/**
 * Returns the changed status of the betslip pick based on newly fetched projections
 * @param pick the pick to check against
 * @param newProjections new fetched projections for this pick
 * @returns the status of the pick projection
 */
export const getProjectionUpdateStatusFromProjections = (
    pick: BetslipPick,
    newProjections: PlayerProjectionFragment[] | undefined
): ProjectionUpdateStatus => {
    const updatedProjection = newProjections?.find(it => areProjectionsMatching(it, pick.projection));

    if (!updatedProjection) {
        //Projection doesn't exist anymore
        return { status: 'removed', projection: pick.projection };
    }

    const defaultVal = { projection: updatedProjection };

    if (!updatedProjection.allowedOptions?.find(it => it.outcome === pick.outcome)) {
        //Projection outcome removed
        logger.debug(
            LOG_TAG,
            `Outcome no longer exists ${updatedProjection.name} ${pick.outcome} ${pick.player.firstName} ${pick.player.lastName}`
        );
        return { status: 'outcome-removed', ...defaultVal };
    }

    const marketStatusChanged =
        !!updatedProjection.marketStatus &&
        !!pick.projection.marketStatus &&
        updatedProjection.marketStatus !== pick.projection.marketStatus;
    if (marketStatusChanged) {
        if (updatedProjection.marketStatus === MarketStatus.Suspended) {
            //Market suspended
            logger.debug(
                LOG_TAG,
                `Market suspended ${updatedProjection.name} ${pick.player.firstName} ${pick.player.lastName}`
            );
            return { status: 'suspended', ...defaultVal };
        } else {
            logger.debug(
                LOG_TAG,
                `Market resumed ${updatedProjection.name} ${pick.player.firstName} ${pick.player.lastName}`
            );
            return { status: 'resumed', ...defaultVal };
        }
    }

    if (updatedProjection.type !== pick.projection.type) {
        //Projection type changed
        return {
            ...defaultVal,
            newType: updatedProjection.type,
            status: 'type-changed',
        };
    }

    if (updatedProjection.type === ProjectionType.Regular) {
        //Check if regular projection value has changed
        const newValue = getNewProjValue(pick.projection.value, updatedProjection.value);
        if (newValue) {
            return {
                ...defaultVal,
                status: 'value-changed',
                newValue,
            };
        }
    } else {
        //Check if special projection value has changed
        const newNonRegularProjVal = getNewProjValue(
            pick.projection.nonRegularValue,
            updatedProjection.nonRegularValue
        );
        if (newNonRegularProjVal) {
            return {
                ...defaultVal,
                status: 'value-changed',
                newValue: newNonRegularProjVal,
            };
        }
    }

    if (checkIfMarketIdsChanged(pick, updatedProjection)) {
        return {
            ...defaultVal,
            status: 'market-id-change',
        };
    }

    //This means projection value was not changed
    return {
        ...defaultVal,
        status: 'valid',
    };
};

/**
 * A projection type change can keep the displayed value unchanged [eg: a special 12.5 becoming a regular 12.5].
 * In this case only the projection type changed, so we shouldn't tell the user the projection values have changed.
 */
export const isOnlyProjectionTypeChanged = ({ pick, projectionStatus }: PickAndEventWithStatus): boolean =>
    projectionStatus.status === 'type-changed' &&
    getDisplayedProjectionValue(pick.projection) === getDisplayedProjectionValue(projectionStatus.projection);

/**
 * True when all the picks with changed projections only had their projection type changed, with the same value
 */
export const haveOnlyProjectionTypesChanged = (picks?: PickAndEventWithStatus[]): boolean =>
    !!picks?.length && picks.every(isOnlyProjectionTypeChanged);

/**
 * Checks if any picks from the current betslip have become invalid, due to changes.
 * The picks we get can fall under 3 categories:
 * 1. invalidPicksForEvent: the event is not valid for the pick OR projection was removed OR outcome changed
 * 2. picksWithMissingEvents: the event does not exist
 * 3. picksWithChangedProjections: the projection value or type changed
 * 4. picksWithChangedMarketStatus: the market got suspended or resumed, so it must be updated in the betslip.
 *    This also covers live picks whose projection disappeared and that we keep in the betslip as suspended.
 */
export const getInvalidPicks = (
    picks: BetslipPick[],
    events: EventInfo[]
): {
    invalidPicksByEvent: InvalidPicksByEvent[];
    picksWithMissingEvents: PickWithStatus[];
    picksWithChangedProjections: PickAndEventWithStatus[];
    picksWithChangedMarketIds: PickAndEventWithStatus[];
    picksWithChangedMarketStatus: PickAndEventWithStatus[];
} => {
    const invalidPicksByEvent: InvalidPicksByEvent[] = [];

    const invalidPicksForEvent: PickWithStatus[] = [];
    const picksWithMissingEvents: PickWithStatus[] = [];
    const picksWithChangedMarketStatus: PickAndEventWithStatus[] = [];
    const picksWithChangedProjections: PickAndEventWithStatus[] = getPicksWithChangeStatus({
        picks,
        events,
        statuses: ['value-changed', 'type-changed'],
    });

    const picksWithChangedMarketIds: PickAndEventWithStatus[] = getPicksWithChangeStatus({
        picks,
        events,
        statuses: ['market-id-change'],
    });

    picks.forEach(pick => {
        const event = events.find(e => e.id === pick.eventId);
        const playerName = `${pick.player.firstName} ${pick.player.lastName}`;
        if (!event) {
            logger.debug(LOG_TAG, 'Event does not exist for pick', playerName);
            picksWithMissingEvents.push({
                pick,
                projectionStatus: { status: 'event-removed', projection: pick.projection },
            });
        } else {
            const projectionStatus = getProjectionUpdateStatus(pick, event);
            if (projectionStatus.status === 'removed') {
                //If this player projection got removed, but the pick is live, we handle those as 'suspended'
                if (keepRemovedProjectionAsSuspended(pick)) {
                    //Also, do not update the betslip if it already contains this pick as suspended
                    if (pick.projection.marketStatus !== MarketStatus.Suspended) {
                        logger.debug(LOG_TAG, 'Projection is live and will be kept as suspended for pick', playerName);
                        picksWithChangedMarketStatus.push({
                            pick,
                            event,
                            projectionStatus: {
                                projection: { ...projectionStatus.projection, marketStatus: MarketStatus.Suspended },
                                status: 'suspended',
                            },
                        });
                    }
                } else {
                    logger.debug(LOG_TAG, 'Projection was removed for pick', playerName);
                    invalidPicksForEvent.push({ pick, projectionStatus: projectionStatus });
                }
            } else if (projectionStatus.status === 'suspended' || projectionStatus.status === 'resumed') {
                logger.debug(LOG_TAG, `Projection ${projectionStatus.status} for pick`, playerName);
                picksWithChangedMarketStatus.push({
                    pick,
                    event,
                    //Keep the existing pick.projection, only updating the market status based on the projection status
                    //so we are not silentily updating the projection value in the betslip if user does not have 'auto-accept' enabled
                    //next run will also update the projection value if it has changed
                    projectionStatus: {
                        ...projectionStatus,
                        projection: { ...pick.projection, marketStatus: projectionStatus.projection.marketStatus },
                    },
                });
            } else if (projectionStatus.status === 'outcome-removed') {
                logger.debug(LOG_TAG, 'Projection outcome changed', playerName);
                invalidPicksForEvent.push({ pick, projectionStatus: projectionStatus });
            } else if (projectionStatus.status === 'type-changed') {
                //Projections that change type are not considered invalid, they will be handled
                //as value changes
                logger.debug(LOG_TAG, 'Projection type changed to:', projectionStatus.newType, playerName);
            }
        }
    });

    events.forEach(e => {
        const invalidPicks = invalidPicksForEvent.filter(it => it.pick.eventId === e.id);
        if (invalidPicks.length > 0) {
            invalidPicksByEvent.push({ event: e, invalidPicks });
        }
    });

    return {
        invalidPicksByEvent,
        picksWithMissingEvents,
        picksWithChangedProjections,
        picksWithChangedMarketIds,
        picksWithChangedMarketStatus,
    };
};

const keepRemovedProjectionAsSuspended = (pick: BetslipPick) => {
    return pick.projection.isLive && shouldKeepFantasyLiveRemovedMarkets();
};

export const getPicksWithChangeStatus = ({
    picks,
    events,
    statuses,
}: {
    picks: BetslipPick[];
    events: EventInfo[];
    statuses: ProjectionInvalidReason[];
}) => {
    const picksWithChangedProjectionValue: PickAndEventWithStatus[] = [];
    picks.forEach(pick => {
        const event = events.find(e => e.id === pick.eventId);
        if (event) {
            const projectionStatus = getProjectionUpdateStatus(pick, event);
            if (statuses.includes(projectionStatus.status)) {
                picksWithChangedProjectionValue.push({
                    event,
                    projectionStatus: projectionStatus,
                    pick: {
                        ...pick,
                        projection: {
                            ...pick.projection,
                            allowedOptions: projectionStatus.projection.allowedOptions,
                        },
                    },
                });
            }
        }
    });
    return picksWithChangedProjectionValue;
};

/**
 * This function updates the current betslip picks based on the changed picks data.
 * Will replace in the existing betslip the picks found in the changed picks data.
 * This updates the selected projection for the betslip picks, and also the betslip players available projections.
 * @returns the updated picks
 */
export const getUpdatedPicks = ({
    betSlipPicks,
    changedPicks,
}: {
    betSlipPicks: BetslipPick[];
    changedPicks: PickAndEventWithStatus[];
}): BetslipPick[] => {
    return betSlipPicks.map(item => {
        const changed = changedPicks.find(
            ({ pick, event }) => item.player.id === pick.player.id && item.eventId === event.id
        );
        if (changed) {
            return {
                ...item,
                projection: { ...changed.projectionStatus.projection },
                outcome: changed.pick.outcome,
                player: { ...item.player, projections: changed.pick.player.projections },
            };
        }
        return item;
    });
};

/**
 * Checks if the market ids or the market option ids have changed between the old and new projection,
 * @param pick the current betslip pick
 * @param newProjection the new projection to compare against
 * @returns true if the market ids or market option ids have changed, false otherwise
 */
export const checkIfMarketIdsChanged = (pick: BetslipPick, newProjection: PlayerProjectionFragment) => {
    if (pick.projection.marketId !== newProjection.marketId) {
        return true;
    }
    const marketOption = pick.projection.allowedOptions?.find(it => it.outcome === pick.outcome);
    const newMarketOption = newProjection.allowedOptions?.find(it => it.outcome === pick.outcome);
    if (marketOption && newMarketOption) {
        return marketOption.marketOptionId !== newMarketOption.marketOptionId;
    }
    return false;
};
